import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import { APIHOST } from '../common';
import DisplayCreditCard from '../components/dialog/DisplayCreditCard';
import { OrderRoom } from '../types/orderRoom';
import { getToken } from '../components/auth/AuthContext';

type Order = {
  id: number;
  documentId: string;
  confirmation?: string | null;
  contactEmail?: string | null;
  contactFirstName?: string | null;
  contactLastName?: string | null;
  paymentMethod?: string | null;
  creditCardAdded?: boolean;
  creditCardMasked?: string | null;
  creditCardProcessedDate?: string | null;
  invoicedRoomSubTotal?: number | string | null;
  invoicedRoomSubtotal?: number | string | null;
  invoicedTaxPerRoomNight?: number | string | null;
  invoicedTaxPerRoomNightTotal?: number | string | null;
  invoiceTotal?: number | string | null;
  invoicedTotal?: number | string | null;
  order_rooms?: OrderRoom[];
};

export default function HotelAdminOrderPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [displayCreditCardOpen, setDisplayCreditCardOpen] = useState(false);

  useEffect(() => {
    if (!orderId) return;
    const token = getToken();
    setLoading(true);
    fetch(`${APIHOST}/api/hotel-admin/orders/${orderId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch order');
        return res.json();
      })
      .then((data) => {
        setOrder(data?.data || null);
        setError(null);
      })
      .catch((err) => {
        setError(err.message);
        setOrder(null);
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="warning">No order data found.</Alert>
      </Container>
    );
  }

  const rows = [
    { label: 'First Name', value: order.contactFirstName || '—' },
    { label: 'Last Name', value: order.contactLastName || '—' },
    { label: 'Email', value: order.contactEmail || '—' },
    { label: 'Payment Method', value: order.paymentMethod || '—' },
  ];
  const formatCurrency = (amount?: number | string | null) => {
    const numericAmount = typeof amount === 'number' ? amount : (typeof amount === 'string' ? Number(amount) : NaN);
    if (!Number.isFinite(numericAmount)) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(numericAmount);
  };
  const getNights = (arrivalDate?: string, departureDate?: string): number | null => {
    if (!arrivalDate || !departureDate) return null;
    const arrival = new Date(arrivalDate);
    const departure = new Date(departureDate);
    const diffMs = departure.getTime() - arrival.getTime();
    if (Number.isNaN(diffMs) || diffMs < 0) return null;
    return Math.round(diffMs / (1000 * 60 * 60 * 24));
  };
  const invoicedRoomSubTotal = order.invoicedRoomSubTotal ?? order.invoicedRoomSubtotal;
  const invoiceTotal = order.invoiceTotal ?? order.invoicedTotal;

  return (
    <Grid container sx={{ px: 4, mt: 8 }} spacing={2}>
      <Grid size={12}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
      </Grid>

      <Grid size={12}>
        <Card variant="outlined">
          <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }} color="text.secondary">
          Order {order.confirmation || '—'}
          </Typography>
          <Table size="small">
          <TableBody>
              {rows.map(({ label, value }) => (
              <TableRow key={label}>
                  <TableCell
                  sx={{ fontWeight: 600, width: '20%', border: 'none' }}
                  >
                  {label}
                  </TableCell>
                  <TableCell sx={{ border: 'none' }}>{value}</TableCell>
              </TableRow>
              ))}
          </TableBody>
          </Table>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={12}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }} color="text.secondary">
              Billing
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Selected Payment Method: {order.paymentMethod || '—'}
              <Tooltip title="The payment method selected to pay for this order."><InfoIcon sx={{ ml: 1, fontSize: 16 }} /></Tooltip>
            </Typography>
            {!order.paymentMethod || order.paymentMethod === '' && (
              <Typography variant="body2" color="text.secondary">
                No payment method selected for this order.
              </Typography>
            )}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Invoiced Room Subtotal: {formatCurrency(invoicedRoomSubTotal)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invoiced Tax Per Room Night: {formatCurrency(order.invoicedTaxPerRoomNight)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invoiced Tax Per Room Night Total: {formatCurrency(order.invoicedTaxPerRoomNightTotal)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Invoice Total: {formatCurrency(invoiceTotal)}
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ my: 3 }} color="text.secondary">
              Payment Card
            </Typography>
            {order.creditCardAdded && <Box>
              <Typography variant="body2" color="text.secondary">
                Credit card ending in {order.creditCardMasked || '—'}.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                  {order.creditCardProcessedDate ? `Credit card processed on ${new Date(order.creditCardProcessedDate).toLocaleDateString()}` : 'Credit card not processed yet.'}
              </Typography>
              <Button
                size="small"
                variant="contained"
                sx={{ mt: 1.5 }}
                onClick={() => setDisplayCreditCardOpen(true)}
              >
                Display Credit Card Info
              </Button>
            </Box>}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={12}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }} color="text.secondary">
              Order Rooms
            </Typography>
            {Array.isArray(order.order_rooms) && order.order_rooms.length > 0 ? (
              <Box>
                {order.order_rooms.map((room) => (
                  (() => {
                    const nights = getNights(room.arrivalDate, room.departureDate);
                    const roomTotal = typeof nights === 'number' && typeof room.dailyRate === 'number'
                      ? room.dailyRate * nights
                      : null;

                    return (
                  <Box
                    key={room.documentId || room.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      px: 1.5,
                      py: 1.25,
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      # {room.confirmation || 'No Confirmation'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {room.conference_hotel_room?.name || 'Unknown room'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Type: {room.type || 'Unknown'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check In: {room.arrivalDate}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Check Out: {room.departureDate}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Nights: {nights ?? 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Daily Rate: {formatCurrency(room.dailyRate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Room Total: {roomTotal === null ? 'N/A' : formatCurrency(roomTotal)}
                    </Typography>

                    {Array.isArray(room.order_room_occupants) && room.order_room_occupants.length > 0 ? (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Occupants:
                        </Typography>
                        {room.order_room_occupants.map((occupant) => (
                          <Typography key={occupant.id} variant="caption" color="text.secondary" display="block">
                            {occupant.firstName} {occupant.lastName}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        No occupants listed.
                      </Typography>
                    )}

                    {room.documentId && (
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mt: 1.5 }}
                        href={`/hotel-admin/room/${room.documentId}`}
                      >
                        View Room
                      </Button>
                    )}
                  </Box>
                    );
                  })()
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No rooms found for this order.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
      <DisplayCreditCard
        open={displayCreditCardOpen}
        onClose={() => setDisplayCreditCardOpen(false)}
        orderId={order.documentId}
      />
    </Grid>
  );
}
